/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CrownIcon,
  TwitchLogoIcon,
  VideoCameraIcon,
  DotIcon,
  TrophyIcon,
  LightningIcon,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const GROUP_META = {
  twitch: { label: "Twitch", color: "#9146FF", icon: <TwitchLogoIcon weight="fill" size={14} /> },
  cam: { label: "Cam", color: "#FF007F", icon: <VideoCameraIcon weight="fill" size={14} /> },
  other: { label: "Other", color: "#00E5FF", icon: <DotIcon weight="fill" size={14} /> },
};

const Leaderboard = () => {
  const [entries, setEntries] = useState([]);
  const [status, setStatus] = useState(null);
  const [groupFilter, setGroupFilter] = useState("all");

  useEffect(() => {
    const refresh = async () => {
      try {
        const [l, s] = await Promise.all([
          api.get("/promo/vip/leaderboard"),
          api.get("/promo/vip"),
        ]);
        setEntries(l.data.entries || []);
        setStatus(s.data);
      } catch (e) { /* noop */ }
    };
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, []);

  const filtered = groupFilter === "all" ? entries : entries.filter((e) => e.platform_group === groupFilter);

  // Top 3 referrers across the whole class (ties broken by position / earliest first)
  const topRefIds = [...entries]
    .filter((e) => (e.referrals || 0) > 0)
    .sort((a, b) => (b.referrals - a.referrals) || (a.position - b.position))
    .slice(0, 3)
    .map((e) => e.position);
  const trophy = (position) => {
    const i = topRefIds.indexOf(position);
    if (i === 0) return { color: "#FFD700", label: "Gold", medal: "🥇" };
    if (i === 1) return { color: "#C0C0C0", label: "Silver", medal: "🥈" };
    if (i === 2) return { color: "#CD7F32", label: "Bronze", medal: "🥉" };
    return null;
  };

  return (
    <div className="min-h-screen bg-[#05050A] text-white">
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-8 py-5 max-w-7xl mx-auto">
        <Link to="/" className="flex items-center gap-2">
          <Logo size={38} withBackground />
          <div className="flex flex-col leading-none">
            <span className="font-display font-black text-lg">LUCRÜ</span>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <Link to="/vip" data-testid="leaderboard-claim-cta">
            <button className="btn-pill btn-primary text-xs"><CrownIcon weight="fill" size={12} /> Claim a slot</button>
          </Link>
        </div>
      </nav>

      <section className="max-w-5xl mx-auto px-6 md:px-8 pt-6 pb-20">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#39FF14]/40 bg-[#39FF14]/5 text-xs text-[#39FF14] mb-5">
            <TrophyIcon weight="fill" size={12} /> Top 300 · Launch class
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-tight leading-[0.95]">
            The <span className="text-gradient">Founders Class</span>
          </h1>
          <p className="text-white/60 mt-4 max-w-xl mx-auto text-sm md:text-base">
            Every streamer who claimed a Pro week in LUCRÜ's launch program — in the order they signed up. Permanent, public, and shareable.
          </p>
        </div>

        {/* Group counters */}
        {status && (
          <div className="grid grid-cols-3 gap-3 mb-6" data-testid="leaderboard-counters">
            {Object.entries(status.quotas || {}).map(([g, total]) => {
              const claimed = (status.claimed_by_group || {})[g] || 0;
              const pct = total > 0 ? (claimed / total) * 100 : 0;
              const meta = GROUP_META[g] || { label: g, color: "#A0A0B0" };
              return (
                <button
                  key={g}
                  onClick={() => setGroupFilter(groupFilter === g ? "all" : g)}
                  data-testid={`counter-${g}`}
                  className={`glass rounded-xl p-4 text-left transition-colors ${
                    groupFilter === g ? "border-2" : "border border-[#222233]"
                  }`}
                  style={{ borderColor: groupFilter === g ? meta.color : undefined }}
                >
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest" style={{ color: meta.color }}>
                    {meta.icon} {meta.label}
                  </div>
                  <div className="font-display text-2xl font-black mt-1">
                    {claimed}<span className="text-white/30 text-sm"> / {total}</span>
                  </div>
                  <div className="h-1 rounded-full bg-[#0F0F16] mt-2 overflow-hidden">
                    <div className="h-full" style={{ width: `${pct}%`, background: meta.color }} />
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Filter chip */}
        {groupFilter !== "all" && (
          <div className="mb-4 flex items-center gap-2 text-xs text-white/60">
            <span>Filtered by {GROUP_META[groupFilter]?.label}</span>
            <button onClick={() => setGroupFilter("all")} className="text-[#00E5FF] underline" data-testid="clear-filter">
              clear
            </button>
          </div>
        )}

        {/* Leaderboard list */}
        <div className="glass rounded-2xl overflow-hidden" data-testid="leaderboard-list">
          <div className="grid grid-cols-12 px-5 py-3 border-b border-[#222233] text-[10px] uppercase tracking-widest text-white/40">
            <div className="col-span-1">#</div>
            <div className="col-span-6">Streamer</div>
            <div className="col-span-3">Platform</div>
            <div className="col-span-2 text-right">Refs</div>
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-5 py-16 text-center text-sm text-white/40">
                No streamers in this group yet — be the first.
              </div>
            )}
            {filtered.map((e, i) => {
              const meta = GROUP_META[e.platform_group] || { label: e.platform || "other", color: "#A0A0B0" };
              const t = trophy(e.position);
              return (
                <motion.div
                  key={`${e.position}-${e.streamer_name}`}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: Math.min(i * 0.015, 0.6) }}
                  className={`grid grid-cols-12 px-5 py-3 border-b border-[#222233] last:border-0 text-sm hover:bg-white/5 ${
                    t ? "bg-gradient-to-r from-transparent to-white/5" : ""
                  }`}
                  data-testid={`leaderboard-row-${e.position}`}
                >
                  <div className="col-span-1 font-mono text-white/50">
                    {e.position <= 3 ? (
                      <span className={e.position === 1 ? "text-[#39FF14]" : e.position === 2 ? "text-[#00E5FF]" : "text-[#FF007F]"}>
                        #{e.position}
                      </span>
                    ) : (
                      <span>#{e.position}</span>
                    )}
                  </div>
                  <div className="col-span-6 font-semibold truncate flex items-center gap-2">
                    <span>{e.streamer_name}</span>
                    {t && (
                      <span
                        className="text-[10px] px-2 py-0.5 rounded-full border font-bold uppercase tracking-wider"
                        style={{ color: t.color, borderColor: t.color }}
                        data-testid={`trophy-${t.label.toLowerCase()}`}
                        title={`${t.label} referrer`}
                      >
                        {t.medal} {t.label}
                      </span>
                    )}
                  </div>
                  <div className="col-span-3 flex items-center gap-1.5 text-xs" style={{ color: meta.color }}>
                    {meta.icon}
                    <span className="capitalize">{e.platform}</span>
                  </div>
                  <div className="col-span-2 text-right text-xs">
                    {e.referrals > 0 ? (
                      <span className="font-semibold" style={{ color: t ? t.color : "#39FF14" }}>+{e.referrals}</span>
                    ) : (
                      <span className="text-white/30">—</span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <div className="text-center mt-8">
          <Link to="/vip">
            <button className="btn-pill btn-primary justify-center" data-testid="leaderboard-bottom-cta">
              <LightningIcon size={14} weight="fill" /> Grab a slot — join the class
            </button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#222233] py-6 text-center text-[10px] text-white/40">
        © 2026 LUCRÜ · LUCRÜ™ is a licensed product of LUCRÜ
      </footer>
    </div>
  );
};

export default Leaderboard;
